import type { ReactNode } from 'react'
import { AppearancePage } from './pages/app-experience/AppearancePage'
import { AppBehaviorPage } from './pages/app-experience/AppBehaviorPage'
import { AccountProfilePage } from './pages/account-data/AccountProfilePage'
import { DataPrivacyPage } from './pages/account-data/DataPrivacyPage'
import { SyncPage } from './pages/account-data/SyncPage'
import { ImportExportPage } from './pages/account-data/ImportExportPage'
import { BackupsPage } from './pages/account-data/BackupsPage'
import { OpenSourcePage } from './pages/help-about/OpenSourcePage'
import { ComingSoonPage } from './stubs/ComingSoonPage'
import type { SlotId, SubNavId, TopNavId } from './settings-tree'
import type { PendingPreferencesAction } from './usePreferencesNavigation'

interface SlotRendererArgs {
  platform: 'electron' | 'web'
  topNavId: TopNavId
  subNavId: SubNavId
  /** Jump to another `(TopNav, SubNav)` slot — lets a page link to a
   *  sibling page (e.g. the Profil entry card → "HappyCloud Sync"). */
  navigate: (topNavId: TopNavId, subNavId: SubNavId) => void
  /** Was diese Seite gleich tun soll (Menü, Doppelklick) — eine Unterseite öffnen. */
  action?: PendingPreferencesAction | null
}

type SlotRenderer = (args: SlotRendererArgs) => ReactNode

/**
 * Slot-id → renderer map. Anything not listed here falls back to the
 * {@link ComingSoonPage} stub in {@link renderSlot}.
 *
 * Adding a new page:
 * 1. Create the page under `pages/<top-nav>/<Name>Page.tsx`
 * 2. Register it here with the matching `${topNavId}/${subNavId}` key
 * 3. Ship i18n keys + (optionally) extend `settings-tree.ts` if the slot
 *    didn't exist yet
 *
 * The key type is `SlotId`, so the TypeScript compiler rejects typos
 * like `'app-experiance/appearance'` or impossible pairs.
 */
const SLOT_REGISTRY: Partial<Record<SlotId, SlotRenderer>> = {
  'app-experience/appearance': () => <AppearancePage />,
  // Web shows only the sound section; display modes and the global shortcut
  // are Electron-only and hidden by the page itself.
  'app-experience/app-behavior': ({ platform }) => <AppBehaviorPage platform={platform} />,
  'account-data/account-profile': ({ navigate, platform }) => (
    <AccountProfilePage platform={platform} onSetupSync={() => navigate('account-data', 'sync')} />
  ),
  'account-data/sync': ({ platform }) => <SyncPage platform={platform} />,
  'account-data/backups': ({ action }) => <BackupsPage action={action} />,
  'account-data/data-privacy': () => <DataPrivacyPage />,
  'account-data/import': ({ platform, action }) => <ImportExportPage platform={platform} action={action} />,
  'help-about/open-source': () => <OpenSourcePage />,
}

/**
 * Ob es für diesen Slot eine ECHTE Seite gibt.
 *
 * Der Wächter des MVP-Schnitts: Seit ihm zeigt keine Seite der Einstellungen
 * mehr „Bald verfügbar", und der Test in `settings-tree.test.ts` hält das
 * fest, indem er jeden Eintrag des Baums hier nachschlägt. Die einzige
 * Ausnahme ist `app-behavior` im Web — der Eintrag ist dort gar nicht
 * sichtbar (`visibleTree`).
 */
export function hasRegisteredPage(id: string): boolean {
  return id in SLOT_REGISTRY
}

/**
 * Resolve a slot id to its rendered content. Returns the registered page
 * for slots that have real content, otherwise the {@link ComingSoonPage}
 * stub. Used by {@link PreferencesShell} as the single dispatch point
 * between navigation state and page components.
 */
export function renderSlot(args: SlotRendererArgs): ReactNode {
  const id: SlotId = `${args.topNavId}/${args.subNavId}`
  const renderer = SLOT_REGISTRY[id]
  if (renderer) return renderer(args)
  return <ComingSoonPage topNavId={args.topNavId} subNavId={args.subNavId} />
}
